import html2canvas from "html2canvas";
import {
  SCREENSHOT_RENDER_DELAY_MS,
  SCREENSHOT_CAPTURE_TIMEOUT_MS,
} from "./constants";

/**
 * Captures a scoped screenshot of the application.
 *
 * Returns null if capture fails (does not throw).
 * This ensures session creation is never blocked by screenshot failures.
 *
 * Common failure scenarios:
 * - Safari security restrictions
 * - iFrame cross-origin restrictions
 * - Memory constraints
 * - Privacy extensions blocking canvas
 *
 * @param rootSelector - CSS selector for the root element to capture (default: '#root')
 * @returns Promise<string | null> - Base64 encoded screenshot or null on failure
 */
export async function captureScopedScreenshot(
  rootSelector: string = "#root"
): Promise<string | null> {
  try {
    const capturePromise = Promise.race([
      (async () => {
        const root = document.querySelector(rootSelector);
        if (!root) {
          return null;
        }

        const rootElement = root as HTMLElement;
        const rootWidth = rootElement.offsetWidth || rootElement.clientWidth;
        const rootHeight = rootElement.offsetHeight || rootElement.clientHeight;

        // Validate element has dimensions
        if (rootWidth === 0 || rootHeight === 0) {
          return null;
        }

        // Wait a bit for any pending renders/images to load
        await new Promise((resolve) =>
          setTimeout(resolve, SCREENSHOT_RENDER_DELAY_MS)
        );

        // Hide inputs and private elements before capture
        const inputs = root.querySelectorAll(
          'input, [data-debug-private="true"]'
        );
        const originalStyles: Array<{ element: HTMLElement; display: string }> =
          [];

        inputs.forEach((el) => {
          const htmlEl = el as HTMLElement;
          originalStyles.push({
            element: htmlEl,
            display: htmlEl.style.display,
          });
          htmlEl.style.display = "none";
        });

        try {
          const canvas = await html2canvas(rootElement, {
            useCORS: true,
            allowTaint: false,
            scale: 1,
            logging: false,
            backgroundColor: "#ffffff",
            removeContainer: false,
            imageTimeout: 30000,
            foreignObjectRendering: false,
            windowWidth: window.innerWidth,
            windowHeight: window.innerHeight,
            scrollX: -window.scrollX,
            scrollY: -window.scrollY,
            onclone: (clonedDoc) => {
              // Ensure cloned document has proper styles
              const clonedRoot = clonedDoc.querySelector(rootSelector);
              if (clonedRoot) {
                const style = (clonedRoot as HTMLElement).style;
                if (style.display === "none") {
                  style.display = "block";
                }
              }
            },
          });

          // Validate canvas has content
          if (canvas.width === 0 || canvas.height === 0) {
            return null;
          }

          // Check if canvas has meaningful content (sample multiple areas)
          const ctx = canvas.getContext("2d");
          if (ctx) {
            // Sample multiple areas: top-left, top-right, center, bottom-left, bottom-right
            const sampleAreas = [
              {
                x: 0,
                y: 0,
                w: Math.min(100, canvas.width),
                h: Math.min(100, canvas.height),
              },
              {
                x: Math.max(0, canvas.width - 100),
                y: 0,
                w: Math.min(100, canvas.width),
                h: Math.min(100, canvas.height),
              },
              {
                x: Math.floor(canvas.width / 2) - 50,
                y: Math.floor(canvas.height / 2) - 50,
                w: 100,
                h: 100,
              },
              {
                x: 0,
                y: Math.max(0, canvas.height - 100),
                w: Math.min(100, canvas.width),
                h: Math.min(100, canvas.height),
              },
              {
                x: Math.max(0, canvas.width - 100),
                y: Math.max(0, canvas.height - 100),
                w: Math.min(100, canvas.width),
                h: Math.min(100, canvas.height),
              },
            ];

            let totalSamples = 0;
            let contentSamples = 0;

            for (const area of sampleAreas) {
              if (
                area.x >= 0 &&
                area.y >= 0 &&
                area.x + area.w <= canvas.width &&
                area.y + area.h <= canvas.height
              ) {
                try {
                  const imageData = ctx.getImageData(
                    area.x,
                    area.y,
                    area.w,
                    area.h
                  );
                  const pixels = imageData.data;
                  totalSamples++;

                  // Check if there are any non-white/transparent pixels in this sample
                  for (let i = 0; i < pixels.length; i += 4) {
                    const r = pixels[i];
                    const g = pixels[i + 1];
                    const b = pixels[i + 2];
                    const a = pixels[i + 3];
                    // Not fully transparent and not pure white (with small tolerance for near-white)
                    if (a > 10 && !(r >= 250 && g >= 250 && b >= 250)) {
                      contentSamples++;
                      break;
                    }
                  }
                } catch (e) {
                  // Skip if we can't sample this area
                }
              }
            }

            // Still return it even if mostly blank - might be a white page, which is valid
          }

          const dataUrl = canvas.toDataURL("image/png");

          // Validate data URL
          if (!dataUrl || dataUrl.length < 100) {
            return null;
          }

          return dataUrl;
        } finally {
          // Restore original styles
          originalStyles.forEach(({ element, display }) => {
            element.style.display = display;
          });
        }
      })(),
      new Promise<null>((resolve) =>
        setTimeout(() => resolve(null), SCREENSHOT_CAPTURE_TIMEOUT_MS)
      ),
    ]);

    // Screenshot is optional, so if it hangs, session creation will continue
    return await capturePromise;
  } catch (error) {
    // Log but don't throw - session creation should continue
    return null;
  }
}
